import { useSetAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { connectionStatusAtom } from '~/atoms/ConnectionState'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'

import { useAnalytics } from './useAnalytics'
import useCloudwatchLogger from './useCloudwatchLogger'

// Use HTMLAudioElement instead of WebAudio. A long-lived AudioContext locks
// to the output device's sample rate at creation time and renders stale
// (metallic screech / underrun burst) when CoreAudio renegotiates on
// macOS sleep/wake or output-device transitions — which is exactly when
// the online/offline events fire.
//
// IMPORTANT: this module's state (pendingSoundTimer, audioCache) is shared
// across all callers. The hook is intended to be mounted exactly once, at
// the app root. Mounting useNetworkConnectivity in multiple components
// simultaneously would interfere with each other's debounce window and
// preloaded element lookups.

// Debounce playback so a flaky network rapidly flapping online↔offline only
// produces one sound for the final settled state, not an overlapping chorus.
// State updates (isOnline, connectionStatus) still fire synchronously on each
// event — the sound and the analytics events are debounced.
const DEBOUNCE_MS = 300
let pendingSoundTimer: ReturnType<typeof setTimeout> | null = null

// Long-lived Audio elements keyed by url. Holding strong references to the
// preloaded elements is what keeps the decoded media in memory — without
// this, GC reclaims the element after preload() returns and a later
// `new Audio(url)` has to re-fetch (which fails for the disconnect sound
// once the network has dropped, see ERR_INTERNET_DISCONNECTED). Reusing
// the same element for playback skips the network entirely.
const audioCache = new Map<string, HTMLAudioElement>()

function playSound(url: string) {
  if (pendingSoundTimer !== null) {
    clearTimeout(pendingSoundTimer)
  }
  pendingSoundTimer = setTimeout(() => {
    pendingSoundTimer = null
    const audio = audioCache.get(url)
    if (!audio) return
    // Reset playback position so a second invocation replays from the
    // start rather than picking up where the previous one left off (or
    // no-opping if it already played to the end).
    audio.currentTime = 0
    audio.play().catch(() => {
      // Online/offline events aren't user gestures; some browsers reject
      // play() in that context. Nothing actionable.
    })
  }, DEBOUNCE_MS)
}

function cancelPendingSound() {
  if (pendingSoundTimer !== null) {
    clearTimeout(pendingSoundTimer)
    pendingSoundTimer = null
  }
}

// Analytics share the sound's debounce rationale: a flapping network fires
// online/offline in rapid alternation, so only the settled state is tracked.
let pendingTrackTimer: ReturnType<typeof setTimeout> | null = null

function trackConnectivitySettled(trackSettledState: () => void) {
  if (pendingTrackTimer !== null) {
    clearTimeout(pendingTrackTimer)
  }
  pendingTrackTimer = setTimeout(() => {
    pendingTrackTimer = null
    trackSettledState()
  }, DEBOUNCE_MS)
}

// Preload the audio file into a long-lived Audio element so a later
// playSound() can reuse it without a network round-trip — critical for the
// disconnect sound, which needs to play after the network has already
// dropped. Module-scoped + idempotent: the elements are created once per
// page load on first hook-effect run, not re-allocated each time the effect
// re-fires (which can happen when consumer dependencies — logger / track —
// re-identify).
function preload(url: string) {
  if (audioCache.has(url)) return
  const audio = new Audio()
  audio.preload = 'auto'
  audio.src = url
  audio.load()
  audioCache.set(url, audio)
}

// Test-only: reset module-scoped singletons so each test starts clean.
// Not exported via the public API — only the test file imports this.
export function _resetForTesting() {
  cancelPendingSound()
  if (pendingTrackTimer !== null) {
    clearTimeout(pendingTrackTimer)
    pendingTrackTimer = null
  }
  audioCache.clear()
}

interface NetworkConnectivityProps {
  playConnectionChangeSound?: boolean
  reportAnalytics?: boolean
}

export function useNetworkConnectivity({
  playConnectionChangeSound = true,
  reportAnalytics = true,
}: NetworkConnectivityProps = {}) {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine)
  const { logger } = useCloudwatchLogger('useConnectivityAlert')
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const setConnectionStatus = useSetAtom(connectionStatusAtom)

  useEffect(() => {
    const handleOnline = () => {
      logger?.info('network connected')
      setIsOnline(true)
      setConnectionStatus('RECONNECTING')
      // After a brief delay, mark as connected if still online
      setTimeout(() => {
        if (navigator.onLine) {
          setConnectionStatus('CONNECTED')
        }
      }, 1000)
      if (reportAnalytics) {
        trackConnectivitySettled(() =>
          track(WebPhoneAnalyticsEvents.NetworkConnected),
        )
      }
      if (playConnectionChangeSound) playSound('/connected.wav')
    }

    const handleOffline = () => {
      logger?.warn('network disconnected')
      setIsOnline(false)
      setConnectionStatus('DISCONNECTED')
      if (reportAnalytics) {
        trackConnectivitySettled(() =>
          track(WebPhoneAnalyticsEvents.NetworkDisconnected),
        )
      }
      if (playConnectionChangeSound) playSound('/disconnected.wav')
    }

    // Preload even when this caller is silent, so the sounds are cached
    // before a call starts on a failing network.
    preload('/connected.wav')
    preload('/disconnected.wav')

    if (navigator.onLine) {
      setConnectionStatus('CONNECTED')
    } else {
      setConnectionStatus('DISCONNECTED')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      // We deliberately do NOT cancelPendingSound() here. This effect
      // re-fires whenever its deps re-identify, and `track` (from
      // useAnalytics) returns a fresh function on every render — so a
      // network event that schedules a sound also triggers a re-render,
      // and an over-eager cleanup would cancel the timer before it
      // fires. The worst case of leaving the timer alive is one stray
      // sound after unmount, which is harmless.
      //
      // The systemic fix is in useAnalytics: wrapping `track` in
      // useCallback would stabilize its identity, letting this effect
      // use proper cleanup semantics. That change would benefit other
      // consumers too and is tracked as a separate follow-up.
    }
  }, [
    playConnectionChangeSound,
    reportAnalytics,
    logger,
    track,
    setConnectionStatus,
  ])

  return isOnline
}
